import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Button,
  Chip,
  CircularProgress,
  Container,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import AssignmentOutlinedIcon from "@material-ui/icons/AssignmentOutlined";
import CloseIcon from "@material-ui/icons/Close";
import SendIcon from "@material-ui/icons/Send";
import { toast } from "react-toastify";

import api from "../../services/api";
import { getSocket } from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SettingsContext } from "../../context/Settings/SettingsContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import useSupportAccess from "../../hooks/useSupportAccess";
import SupportThread from "./SupportThread";
import {
  MAX_IMAGES,
  MAX_TEXT,
  clearDraft,
  loadDraft,
  saveDraft,
  validateImage,
} from "./supportDraft";

const TEAL = "#128C7E";

const useStyles = makeStyles(theme => ({
  root: { paddingTop: theme.spacing(2), paddingBottom: theme.spacing(4) },
  title: { fontWeight: 600, color: TEAL },
  sub: { color: theme.palette.text.secondary, marginBottom: theme.spacing(2) },
  paper: { padding: theme.spacing(2), marginBottom: theme.spacing(3) },
  company: { fontSize: 13, color: theme.palette.text.secondary, marginBottom: theme.spacing(1) },
  notice: {
    background: theme.palette.type === "dark" ? "#3b3320" : "#fff8e1",
    borderLeft: "3px solid #f9a825",
    padding: theme.spacing(1, 1.5),
    fontSize: 13,
    margin: theme.spacing(1.5, 0),
    borderRadius: 4,
  },
  fields: { display: "flex", gap: theme.spacing(1.5), flexWrap: "wrap", marginTop: theme.spacing(1.5) },
  field: { flex: "1 1 220px", minWidth: 0 },
  chips: { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: theme.spacing(1) },
  actions: { display: "flex", gap: theme.spacing(1), flexWrap: "wrap", alignItems: "center", marginTop: theme.spacing(1.5) },
  spacer: { flex: 1 },
  counter: { fontSize: 12, color: theme.palette.text.secondary },
  send: { background: TEAL, color: "#fff", "&:hover": { background: "#0e6e63" }, "&.Mui-disabled": { background: theme.palette.action.disabledBackground, color: theme.palette.action.disabled } },
  previews: { display: "flex", gap: 10, flexWrap: "wrap", marginTop: theme.spacing(1.5) },
  preview: { position: "relative", width: 96, height: 96 },
  previewImg: { width: 96, height: 96, objectFit: "cover", borderRadius: 6, border: `1px solid ${theme.palette.divider}` },
  previewX: {
    position: "absolute",
    top: -8,
    right: -8,
    background: theme.palette.background.paper,
    boxShadow: "0 1px 3px rgba(0,0,0,.35)",
    padding: 2,
    "&:hover": { background: theme.palette.background.paper },
  },
  legend: { fontSize: 12, color: theme.palette.text.secondary, marginTop: theme.spacing(1) },
  hint: { fontSize: 12, color: theme.palette.text.secondary },
  listHead: { display: "flex", alignItems: "center", marginBottom: theme.spacing(1.5) },
}));

const imageFromBlob = (blob, idx = 0) => {
  const ext = blob.type === "image/jpeg" ? "jpg" : blob.type === "image/webp" ? "webp" : "png";
  return new File([blob], `captura-${Date.now()}-${idx}.${ext}`, { type: blob.type });
};

const Support = () => {
  const classes = useStyles();
  const location = useLocation();
  const allowed = useSupportAccess();
  const { user } = useContext(AuthContext);
  const { getSetting } = useContext(SettingsContext);
  const { whatsApps } = useContext(WhatsAppsContext);

  const initial = useMemo(() => loadDraft(user), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [text, setText] = useState(initial.text);
  const [ticket, setTicket] = useState(initial.ticket);
  const [ticketIdInput, setTicketIdInput] = useState("");
  const [whatsappId, setWhatsappId] = useState(initial.whatsappId);
  const [images, setImages] = useState(initial.images); // [{file, url}]
  const [sending, setSending] = useState(false);

  const [requests, setRequests] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const textRef = useRef(null);
  const fileRef = useRef(null);
  const imagesRef = useRef(images);
  imagesRef.current = images;

  const clipboardCanRead = typeof navigator !== "undefined" && !!navigator.clipboard && typeof navigator.clipboard.read === "function";

  // --- borrador: se guarda en cada cambio (sobrevive a navegar y volver) ---
  useEffect(() => {
    saveDraft(user, { text, ticket, whatsappId, images });
  }, [user, text, ticket, whatsappId, images]);

  // Foco en el cuadro al llegar desde el botón del chat (texto vacío y enfocado).
  useEffect(() => {
    if (textRef.current) textRef.current.focus();
  }, [location.key]);

  // --- imágenes ---
  const addFiles = useCallback(files => {
    const next = [...imagesRef.current];
    let firstErr = null;
    for (const f of files) {
      const err = validateImage(f, next.length);
      if (err) {
        firstErr = firstErr || err;
        continue;
      }
      next.push({ file: f, url: URL.createObjectURL(f) });
    }
    if (firstErr) toast.error(i18n.t(firstErr));
    if (next.length !== imagesRef.current.length) setImages(next);
  }, []);

  const removeImage = idx => {
    setImages(prev => {
      const x = prev[idx];
      if (x) URL.revokeObjectURL(x.url);
      return prev.filter((_, i) => i !== idx);
    });
  };

  // Pegar (Ctrl+V / Cmd+V) en TODA la pantalla: sólo se intercepta si el
  // portapapeles trae imágenes; el texto pegado sigue su camino normal.
  useEffect(() => {
    const onPaste = e => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const files = [];
      for (let i = 0; i < items.length; i++) {
        if (items[i].kind === "file" && items[i].type.startsWith("image/")) {
          const f = items[i].getAsFile();
          if (f) files.push(f.name && /\.(png|jpe?g|webp)$/i.test(f.name) ? f : imageFromBlob(f, i));
        }
      }
      if (!files.length) return;
      e.preventDefault();
      addFiles(files);
    };
    document.addEventListener("paste", onPaste);
    return () => document.removeEventListener("paste", onPaste);
  }, [addFiles]);

  const pasteFromButton = async () => {
    if (!clipboardCanRead) {
      toast.info(i18n.t("support.pasteUnsupported"));
      return;
    }
    try {
      const items = await navigator.clipboard.read();
      const files = [];
      for (const it of items) {
        const type = it.types.find(t => t.startsWith("image/"));
        if (type) files.push(imageFromBlob(await it.getType(type), files.length));
      }
      if (!files.length) {
        toast.info(i18n.t("support.pasteNoImage"));
        return;
      }
      addFiles(files);
    } catch (err) {
      if (err && (err.name === "NotAllowedError" || err.name === "SecurityError")) {
        toast.warn(i18n.t("support.pasteDenied"));
      } else if (err && err.name === "NotFoundError") {
        toast.info(i18n.t("support.pasteNoImage"));
      } else {
        toast.info(i18n.t("support.pasteUnsupported"));
      }
    }
  };

  // --- lista ---
  const fetchList = useCallback(async (pageNumber, append) => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/support/requests", { params: { pageNumber } });
      setRequests(prev => (append ? [...prev, ...data.requests.filter(r => !prev.some(p => p.id === r.id))] : data.requests));
      setHasMore(!!data.hasMore);
      setListError(false);
    } catch (err) {
      setListError(true);
      toastError(err);
    }
    setLoadingList(false);
  }, []);

  useEffect(() => {
    if (allowed) fetchList(1, false);
  }, [allowed, fetchList]);

  // Tiempo real: señal liviana `support` (sin texto) a company-<id>.
  useEffect(() => {
    if (!allowed) return undefined;
    const socket = getSocket();
    let timer = null;
    const onSupport = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        setRefreshKey(k => k + 1);
        fetchList(1, false);
        setPage(1);
      }, 400);
    };
    socket.on("support", onSupport);
    return () => {
      clearTimeout(timer);
      socket.off("support", onSupport);
    };
  }, [allowed, fetchList]);

  // --- envío ---
  const canSend = text.trim().length > 0 && !sending;

  const applyTicketId = () => {
    const n = Number(ticketIdInput);
    if (Number.isInteger(n) && n > 0) {
      setTicket({ id: n, contactId: null, contactName: "", whatsappName: "" });
      setTicketIdInput("");
    }
  };

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("text", text.trim());
      if (ticket?.id) fd.append("ticketId", String(ticket.id));
      if (whatsappId) fd.append("whatsappId", String(whatsappId));
      images.forEach(i => fd.append("attachments", i.file));
      const { data } = await api.post("/support/requests", fd);
      toast.success(i18n.t("support.sentOk", { id: data?.request?.id }));
      images.forEach(i => URL.revokeObjectURL(i.url));
      clearDraft(user);
      setText("");
      setTicket(null);
      setWhatsappId("");
      setImages([]);
      setPage(1);
      await fetchList(1, false);
      if (data?.request?.id) setOpenId(data.request.id);
    } catch (err) {
      // el borrador NO se pierde si falla
      toastError(err);
    }
    setSending(false);
  };

  // Sin permiso/flag no hay pantalla. SettingsContext arranca vacío (todo_058),
  // así que NO se redirige: redirigir en esa ventana sacaría al admin de /soporte
  // en cada F5. El gate real es el del backend.
  if (!allowed) return null;

  const companyName = getSetting("businessName") || "";
  const ticketDesc = ticket
    ? [i18n.t("support.ticketPrefilled", { id: ticket.id }), ticket.contactName, ticket.whatsappName].filter(Boolean).join(" · ")
    : "";

  return (
    <Container maxWidth="md" className={classes.root}>
      <Typography variant="h5" component="h1" className={classes.title}>
        {i18n.t("support.title")}
      </Typography>
      <Typography variant="body2" className={classes.sub}>
        {i18n.t("support.subtitle")}
      </Typography>

      <Paper variant="outlined" className={classes.paper}>
        {companyName && (
          <div className={classes.company}>
            {i18n.t("support.company")}: <b>{companyName}</b>
          </div>
        )}
        <TextField
          label={i18n.t("support.yourQuery")}
          placeholder={i18n.t("support.placeholder")}
          variant="outlined"
          fullWidth
          multiline
          rows={5}
          rowsMax={12}
          value={text}
          onChange={e => setText(e.target.value.slice(0, MAX_TEXT))}
          inputRef={textRef}
          InputLabelProps={{ shrink: true }}
          inputProps={{ maxLength: MAX_TEXT }}
        />
        <div className={classes.counter} style={{ textAlign: "right", marginTop: 4 }} aria-live="polite">
          {i18n.t("support.counter", { count: text.length, max: MAX_TEXT })}
        </div>
        <div className={classes.notice} role="note">
          {i18n.t("support.privacy")}
        </div>

        {ticket ? (
          <div className={classes.chips}>
            <Chip
              icon={<AssignmentOutlinedIcon />}
              label={ticketDesc}
              onDelete={() => setTicket(null)}
              deleteIcon={<CloseIcon aria-label={i18n.t("support.removeTicket")} />}
              style={{ maxWidth: "100%" }}
            />
          </div>
        ) : null}

        <div className={classes.fields}>
          {!ticket && (
            <TextField
              className={classes.field}
              label={i18n.t("support.ticketLabel")}
              variant="outlined"
              size="small"
              type="number"
              value={ticketIdInput}
              onChange={e => setTicketIdInput(e.target.value)}
              onBlur={applyTicketId}
              onKeyDown={e => e.key === "Enter" && (e.preventDefault(), applyTicketId())}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: 1 }}
            />
          )}
          <TextField
            select
            className={classes.field}
            label={i18n.t("support.connectionLabel")}
            variant="outlined"
            size="small"
            value={whatsappId}
            onChange={e => setWhatsappId(e.target.value)}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">{i18n.t("support.connectionNone")}</MenuItem>
            {(whatsApps || []).map(w => (
              <MenuItem key={w.id} value={String(w.id)}>
                {w.name}
              </MenuItem>
            ))}
          </TextField>
        </div>

        {images.length > 0 && (
          <div className={classes.previews}>
            {images.map((im, idx) => (
              <div key={im.url} className={classes.preview}>
                <img src={im.url} alt="" className={classes.previewImg} />
                <IconButton size="small" className={classes.previewX} onClick={() => removeImage(idx)} aria-label={i18n.t("support.removeImage")}>
                  <CloseIcon style={{ fontSize: 16 }} />
                </IconButton>
              </div>
            ))}
          </div>
        )}

        <div className={classes.actions}>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            multiple
            hidden
            onChange={e => {
              addFiles(Array.from(e.target.files || []));
              e.target.value = "";
            }}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<AttachFileIcon />}
            onClick={() => fileRef.current && fileRef.current.click()}
            disabled={images.length >= MAX_IMAGES}
          >
            {i18n.t("support.attach")}
          </Button>
          <Tooltip title={clipboardCanRead ? "" : i18n.t("support.pasteUnsupported")}>
            <span>
              <Button variant="outlined" size="small" onClick={pasteFromButton} disabled={images.length >= MAX_IMAGES}>
                {i18n.t("support.pasteImage")}
              </Button>
            </span>
          </Tooltip>
          <span className={classes.hint}>{i18n.t("support.imagesCount", { count: images.length, max: MAX_IMAGES })}</span>
          <span className={classes.spacer} />
          <Button
            variant="contained"
            className={classes.send}
            disabled={!canSend}
            onClick={handleSend}
            endIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          >
            {sending ? i18n.t("support.sending") : i18n.t("support.send")}
          </Button>
        </div>
        <div className={classes.hint} style={{ marginTop: 8 }}>{i18n.t("support.pasteHint")}</div>
        <div className={classes.legend}>{i18n.t("support.legend")}</div>
      </Paper>

      <div className={classes.listHead}>
        <Typography variant="h6" component="h2" style={{ flex: 1 }}>
          {i18n.t("support.myQueries")}
        </Typography>
        <Button size="small" onClick={() => { setPage(1); setRefreshKey(k => k + 1); fetchList(1, false); }}>
          {i18n.t("support.refresh")}
        </Button>
      </div>

      {requests.map(r => (
        <SupportThread
          key={r.id}
          request={r}
          defaultOpen={r.id === openId}
          refreshKey={refreshKey}
          onChanged={() => fetchList(1, false)}
        />
      ))}
      {loadingList && <CircularProgress size={24} />}
      {!loadingList && listError && <Typography color="error">{i18n.t("support.loadError")}</Typography>}
      {!loadingList && !listError && requests.length === 0 && (
        <Typography color="textSecondary">{i18n.t("support.empty")}</Typography>
      )}
      {hasMore && !loadingList && (
        <Button
          fullWidth
          onClick={() => {
            const n = page + 1;
            setPage(n);
            fetchList(n, true);
          }}
        >
          {i18n.t("support.loadMore")}
        </Button>
      )}
    </Container>
  );
};

export default Support;
